// ============================================================
// RBAC Models
// Y/N string flags are kept as literal unions to match the API
// exactly (rather than converting to boolean) so payloads sent
// back to the backend never need re-mapping.
// ============================================================

export type YN = 'Y' | 'N';

export interface Role {
  id?: string;
  _id?: string;
  type: string;           // e.g. "AA"
  name: string;
  landing_page: string;
  mode: string;
  category?: string;       // 'super' | 'normal'
  status?: YN;             // used on update (activate/deactivate)
}

export interface Module {
  id?: string;
  _id?: string;
  name: string;
  url: string;
  order: number;
  sub_module: YN;
  tab: YN;
  category?: string;
  parent_id?: string;        // ADD — always '' for a Module
  status?: YN;
}

export interface ModuleGetParams {
  sorting?: string;
  sorting_order?: string;
}

export interface SubModule {
  id?: string;
  _id?: string;
  name: string;
  url: string;
  order: number;
  module_id: string;         // keep for now — see note below
  parent_id?: string;        // ADD — this is what's actually sent/read now
  is_view: YN;
  is_add: YN;
  is_edit: YN;
  category?: string;
  status?: YN;
}

export interface SubModuleGetParams {
  module_id: string;
}

// ── Inner Module ──────────────────────────────────────────────
// Third level: sits under a SubModule. Can be of type 'modal'
// (form-based, like configuration tabs) or 'table' (future).

export interface ModalFieldDef {
  id: string;
  class?: string;
  value?: string;
  placeholder?: string;
  dropdown?: boolean;
  options?: any[];
}

export interface InnerModule {
  id?: string;
  _id?: string;
  name: string;
  render_type: string;       // RENAMED from `type` — was colliding with Configuration's M/S/I discriminator
  sub_module_id: string;
  parent_id?: string;        // ADD
  module_id: string;
  order: number;
  modalFields?: ModalFieldDef[];
  apiUrl?: string;
  category?: string;
  status?: YN;
}

export interface InnerModuleGetParams {
  sub_module_id: string;
}

// ── Permission ────────────────────────────────────────────────

export interface Permission {
  id?: string;
  role_id: string;
  status: YN;
  config_id: string;   // the module's, sub-module's, or inner-module's own _id
  is_view: YN;
  is_add: YN;
  is_edit: YN;
}

export interface PermissionPayload {
  permission: Permission[];
}

export interface DeletePayload {
  id: string;
}

// ── Permission Matrix UI shapes ───────────────────────────────

/** Row for an inner module in the permission matrix */
export interface InnerModulePermissionRow {
  innerModule: InnerModule;
  permission: Permission | null;
}

/** Row for a submodule — now includes nested inner module rows */
export interface SubModulePermissionRow {
  subModule: SubModule;
  permission: Permission | null;
  innerModuleRows: InnerModulePermissionRow[];
}

/** Top-level group: module with nested submodule rows (each having inner module rows) */
export interface ModulePermissionGroup {
  module: Module;
  modulePermission: Permission | null;
  rows: SubModulePermissionRow[];
}

export interface HierarchicalRoute {
  path: string;
  label: string;
  children?: HierarchicalRoute[];
}

export const HIERARCHICAL_ROUTES: HierarchicalRoute[] = [
  {
    path: '/site_summary',
    label: 'Site Summary',
    children: [
      { path: '/site', label: 'Total Site' },
      { path: '/consumer', label: 'Total Consumer' },
      { path: '/gateway', label: 'Total Gateway' },
      { path: '/cb', label: 'Total CB' },
      { path: '/meter', label: 'Total Meter' },
      { path: '/cut_meter', label: 'Cut Meter "No"' },
      { path: '/ticket_generated', label: 'Total Tickets Generated' },
      { path: '/woq', label: 'Total W.O.Q' },
      { path: '/DG', label: 'DG ON' },
      { path: '/GRID', label: 'GRID ON' },
      { path: '/zeroReading', label: 'Zero Reading' },
      { path: '/sensorCutOff', label: 'Meter Cut off' },
      { path: '/cbNotConnected', label: 'Cb Not Conencted' },
      { path: '/dlNotConnected', label: 'Gateway Not Connected' },
      { path: '/ConsumptionWORecharge', label: 'Consumption Without Recharge' },
      { path: '/GridOverload', label: 'Grid Overload' },
      { path: '/DGOverload', label: 'DG Overload' },
      { path: '/lowbalance', label: 'Low Balance' }
    ]
  },
  {
    path: '/dashboard',
    label: 'Dashboard',
    children: [
      {
        path: '/site_summary',
        label: 'Site Summary',
        children: [
          { path: '/site', label: 'Total Site' },
          { path: '/consumer', label: 'Total Consumer' },
          { path: '/gateway', label: 'Total Gateway' },
          { path: '/cb', label: 'Total CB' },
          { path: '/meter', label: 'Total Meter' },
          { path: '/cut_meter', label: 'Cut Meter "No"' },
          { path: '/ticket_generated', label: 'Total Tickets Generated' },
          { path: '/woq', label: 'Total W.O.Q' },
          { path: '/DG', label: 'DG ON' },
          { path: '/GRID', label: 'GRID ON' },
          { path: '/zeroReading', label: 'Zero Reading' },
          { path: '/sensorCutOff', label: 'Meter Cut off' },
          { path: '/cbNotConnected', label: 'Cb Not Conencted' },
          { path: '/dlNotConnected', label: 'Gateway Not Connected' },
          { path: '/ConsumptionWORecharge', label: 'Consumption Without Recharge' },
          { path: '/GridOverload', label: 'Grid Overload' },
          { path: '/DGOverload', label: 'DG Overload' },
          { path: '/lowbalance', label: 'Low Balance' }
        ]
      },
      { path: '/ev_summary', label: 'EV Summary' },
      { path: '/ev_charger', label: 'EV Charger Detail' },
      { path: '/alarm_summary', label: 'Alarm Summary' },
      { path: '/recharge_summary', label: 'Recharge Summary' },
      { path: '/consumer_details', label: 'Consumer Details' }
    ]
  },
  {
    path: '/reports',
    label: 'Reports',
    children: [
      { path: '/recharge_report', label: 'Recharge Report' },
      { path: '/monthly_max_demand', label: 'Monthly Max Demand' },
      { path: '/monthly_report', label: 'Monthly Report' },
      { path: '/daily_report', label: 'Daily Report' },
      { path: '/debit-credit_report', label: 'Debit-Credit Report' },
      { path: '/billing_report', label: 'Billing Report' },
      { path: '/loadtype-master', label: 'Load Type Master' },
      { path: '/site-user-report', label: 'Site User Report' },
      { path: '/assets-master', label: 'Assets Master' },
      { path: '/reading-calibration', label: 'Reading Calibration' },
      { path: '/member-details', label: 'Member Details' },
      { path: '/zero-reading', label: 'Zero Reading' },
      { path: '/un-healthy-meter', label: 'Unhealthy Meter' },
      { path: '/meter-configuration', label: 'Meter Configuration Report' },
      { path: '/cut-meter-list', label: 'Cut Meter List Report' },
      { path: '/engineer-list', label: 'Engineer List' },
      { path: '/hourly-consumption-report', label: 'Hourly Consumption Report' },
      { path: '/zero-consumption-report', label: 'Zero Consumption Report' },
      { path: '/current-data-report', label: '/current-data-report' }, // ⚠️ see note below
      { path: '/process-data-report', label: 'Process Data' },
      { path: '/monthly-billing-report', label: 'Monthly Statement' },
      { path: '/sitelogin-report', label: '/sitelogin-report' }, // ⚠️ see note below
      { path: '/dg-running-reports', label: 'Detailed DG Running Report' }
    ]
  },
  {
    path: '/assets',
    label: 'Assets',
    children: [
      { path: '/application', label: '/application' },
      { path: '/processor', label: '/processor' },
      { path: '/site_solution_type', label: '/site_solution_type' },
      { path: '/communications', label: '/communications' },
      { path: '/customer', label: '/customer' },
      { path: '/license', label: '/license' },
      { path: '/sim', label: '/sim' },
      { path: '/billing', label: '/billing' },
      { path: '/gateway', label: '/gateway' },
      { path: '/meter', label: '/meter' },
      { path: '/cb', label: '/cb' },
      { path: '/modem', label: '/modem' },
      { path: '/sensor', label: '/sensor' },
      { path: '/brand', label: '/brand' },
      { path: '/datalogger', label: '/datalogger' },
      { path: '/bulk_command', label: '/bulk_command' }
    ]
  },
  {
    path: '/configuration',
    label: 'Configuration',
    children: [
      { path: '/site_configuration', label: 'Site Configuration' },
      { path: '/hardware', label: 'Hardware' },
      { path: '/slab_configuration', label: 'Slab Configuration' },
      { path: '/billing', label: 'Billing' },
      { path: '/access', label: 'Access' },
      { path: '/administrator', label: 'Administrator' },
      { path: '/sodality', label: 'Sodality' },
      { path: '/master_action', label: 'Master Action' },
      { path: '/asset_entry', label: 'Asset Entry' }
    ]
  },
  {
    path: '/logs',
    label: 'Logs',
    children: [
      { path: '/alarms', label: 'Alarms Logs' },
      { path: '/recharge_logs', label: 'Recharge Logs' },
      { path: '/meter_change', label: 'Meter Change Logs' },
      { path: '/dg_running', label: 'DG Running Logs' },
      { path: '/device_actions', label: 'Device Actions' },
      { path: '/load_survey', label: 'Load Survey' },
      { path: '/sms-email', label: 'SMS Logs' },
      { path: '/loadtype_change', label: 'Load Type Change' }
    ]
  },
  {
    path: '/group_action',
    label: 'Group Action',
    children: [
      { path: '/restore', label: 'Restore' },
      { path: '/cut', label: 'Cut' }
    ]
  },
  {
    path: '/log_viewer',
    label: 'Log Viewer',
  },
  {
    path: '/recharge',
    label: 'Recharge',
  },
  {
    path: '/coupon_recharge',
    label: 'Coupon Recharge',
    children: [
      { path: '/generate_coupon', label: 'Generate Coupon' },
      { path: '/recharge', label: 'Recharge' }
    ]
  },
  { path: '/dashboard/recharge_summary', label: 'Dashboard/Recharge Summary' },
  { path: '/dashboard/ev_summary', label: 'Dashboard/EV Summary' },
  { path: '/dashboard/alarm_summary', label: 'Dashboard/Alarm Summary' },
  { path: '/administrator', label: 'Administrator' },
  { path: '/permission', label: 'Permissions' },
  { path: '/comp_build', label: '/comp_build' },
  { path: '/ev_summary', label: 'EV Summary' },
  { path: '/alarm_summary', label: 'Alarm Summary' },
  { path: '/consumer_search', label: 'Consumer Search' },
  { path: '/ConsumerDetail', label: 'Consumer Detail' },
  { path: '/recharge_summary', label: 'Recharge Summary' },
  { path: '/addsites', label: 'Add Sites' },
  { path: '/editsites', label: 'Edit Sites' },
  { path: '/bulkaction', label: 'Bulk Action' },
  { path: '/CreateBulkAction', label: 'Create Bulk Action' }
];

export function findRouteByPath(routes: HierarchicalRoute[], path: string): HierarchicalRoute | null {
  if (!path) return null;
  const cleanPath = path.startsWith('/') ? path : '/' + path;
  for (const r of routes) {
    if (r.path === cleanPath || r.path === path) return r;
    if (r.children) {
      const found = findRouteByPath(r.children, path);
      if (found) return found;
    }
  }
  return null;
}

export function flattenRoutes(routes: HierarchicalRoute[]): { label: string, value: string }[] {
  const list: { label: string, value: string }[] = [];
  const traverse = (r: HierarchicalRoute) => {
    list.push({ label: r.label, value: r.path });
    if (r.children) {
      r.children.forEach(traverse);
    }
  };
  routes.forEach(traverse);
  return list;
}
